import type { ReactiveController, ReactiveControllerHost } from 'lit'
import type { InputMixinInterface } from '../mixins/InputMixin.js'

interface FormDataOptions {
  value: () => string | undefined
  disabled?: () => boolean
}

export class FormDataController implements ReactiveController {
  private _form: HTMLFormElement | null = null
  private initialValue = ''
  private initialValueCaptured = false

  constructor(
    private host: ReactiveControllerHost & InputMixinInterface,
    private options: FormDataOptions,
  ) {
    host.addController(this)
  }

  hostConnected() {
    if (!this.initialValueCaptured) {
      this.initialValue = this.host.value
      this.initialValueCaptured = true
    }
    this.listen(this.host.form)
  }

  hostUpdated() {
    this.listen(this.host.form)
  }

  hostDisconnected() {
    this.cleanup()
  }

  private listen(form: HTMLFormElement | null) {
    if (this._form !== form) {
      this.cleanup()
      this._form = form
      this._form?.addEventListener('formdata', this.handleFormData)
      this._form?.addEventListener('reset', this.handleReset)
    }
  }

  private cleanup() {
    this._form?.removeEventListener('formdata', this.handleFormData)
    this._form?.removeEventListener('reset', this.handleReset)
    this._form = null
  }

  private handleFormData = (e: FormDataEvent) => {
    const { name } = this.host
    const disabled = this.options.disabled?.() ?? this.host.disabled

    if (disabled) {
      return
    }
    if (!name) {
      return
    }

    const value = this.options.value()
    if (value != null) {
      e.formData.append(name, value)
    }
  }

  private handleReset = (event: Event) => {
    const form = event.currentTarget
    queueMicrotask(() => {
      if (!event.defaultPrevented && this._form === form) {
        this.host.value = this.initialValue
      }
    })
  }
}
